import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FormGroup, Input, Label } from 'reactstrap';

import apiOffers from 'api/offers';
import {
  IOfferSigner,
  OfferDefaultsKind,
  OfferDefaultsSource,
} from 'api/interfaces/offers/IOfferDefaults';
import IOfferInfoRow from 'api/interfaces/offers/IOfferInfoRow';
import PageContainer from 'components/layout/PageContainer';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import UserSelect from 'components/layout/forms/UserSelect';
import translations from 'services/translations/translations';
import { COLLATERAL, useChoiceList } from 'services/offers/offerChoices';
import { addMessage } from 'store/messages/actions';

import OfferInfoRowsEditor from './src/offer/OfferInfoRowsEditor';

import 'style/offers/OfferDefaults.css';

const emptyForm: {
  attachmentsNote: string;
  cargo: string;
  collateralType: string;
  country: string;
  deliveryTermsTemplateId: string;
  deliveryTimeNote: string;
  infoRows: IOfferInfoRow[];
  invoiceNote: string;
  offerTextTemplateId: string;
  paymentTerm: string;
  priceListTemplateId: string;
  promoTextEn: string;
  promoTextFi: string;
  promoTextSv: string;
  signatureUser: { label: string; value: string };
  validity: string;
} = {
  attachmentsNote: '',
  cargo: '',
  collateralType: '',
  country: '',
  deliveryTermsTemplateId: '',
  deliveryTimeNote: '',
  infoRows: [],
  invoiceNote: '',
  offerTextTemplateId: '',
  paymentTerm: '',
  priceListTemplateId: '',
  promoTextEn: '',
  promoTextFi: '',
  promoTextSv: '',
  signatureUser: { label: '', value: '' },
  validity: '',
};

interface IChoice {
  id: number;
  name: string;
}

const DELIVERY_TERMS = 'delivery-terms';

// The advertisement above the signature is printed in the language of the offer
const PROMO_LANGUAGES = [
  { field: 'promoTextFi', label: 'Suomi' },
  { field: 'promoTextSv', label: 'Svenska' },
  { field: 'promoTextEn', label: 'English' },
] as const;

const idOrNull = (id: string): null | number =>
  id === '' ? null : parseInt(id, 10);

/**
 * The information the new offers and preliminary offers of a department get, laid out in
 * the order and form of the end of the offer PDF: the offer text, the information table from
 * the attachments to the collaterals, the advertisement and the signature, then the
 * additional price list and the delivery terms on pages of their own. The offer can change
 * them; the offers made earlier keep their own. The advertisement is printed from here.
 */
const OfferDefaults = (): JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation([translations.offers, translations.common]);
  const tr = (key: string) => t(`${translations.offers}:defaults.${key}`);

  // The departments whose offers the user may write
  const [departments, setDepartments] = useState<
    { id: number; name: string }[]
  >([]);
  const [departmentId, setDepartmentId] = useState<number>();
  // Offers and preliminary offers share their defaults, material offers have their own
  const [kind, setKind] = useState<OfferDefaultsKind>('offer');
  // The defaults of one user of the department; none for the whole department
  const [user, setUser] = useState<{ label: string; value: string }>({
    label: '',
    value: '',
  });
  // Whose the shown defaults are, e.g. those of the department until the user has own
  const [source, setSource] = useState<OfferDefaultsSource>('none');
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  // The collaterals the admins manage, in the order of the basic information
  const collateralTypes = useChoiceList(COLLATERAL, form.collateralType);
  // The signer as printed below the signature
  const [signer, setSigner] = useState<IOfferSigner | null>(null);
  // The texts of the archive and the price lists of the registry to choose from
  const [offerTexts, setOfferTexts] = useState<IChoice[]>([]);
  const [deliveryTerms, setDeliveryTerms] = useState<IChoice[]>([]);
  const [priceLists, setPriceLists] = useState<IChoice[]>([]);

  useEffect(() => {
    const toChoices = (list: { id: number; name: string }[]) =>
      list.map((item) => ({ id: item.id, name: item.name }));

    apiOffers.textTemplates
      .get()
      .then((texts) => setOfferTexts(toChoices(texts)))
      .catch(() => setOfferTexts([]));
    apiOffers.textTemplates
      .get(undefined, DELIVERY_TERMS)
      .then((texts) => setDeliveryTerms(toChoices(texts)))
      .catch(() => setDeliveryTerms([]));
    apiOffers.priceListTemplates
      .get()
      .then((lists) =>
        setPriceLists(
          lists.map((list) => ({
            id: list.id,
            name: `${list.name} (${list.rowCount})`,
          }))
        )
      )
      .catch(() => setPriceLists([]));
  }, []);

  useEffect(() => {
    // The user's own department first, where the user's new offers are made
    Promise.all([
      apiOffers.departments.get(true),
      apiOffers.defaults.get({ exact: true }),
    ])
      .then(([writable, own]) => {
        setDepartments(writable);
        setDepartmentId(
          writable.find(
            (department) => department.id === own.organizationUnitId
          )?.id ?? writable[0]?.id
        );
      })
      .catch(() => setDepartments([]));
  }, []);

  const load = () => {
    if (departmentId === undefined) {
      return;
    }

    apiOffers.defaults
      .get({
        exact: true,
        kind,
        organizationUnitId: departmentId,
        userId: user.value || null,
      })
      .then((defaults) => {
        setSource(defaults.source);
        setForm({
          attachmentsNote: defaults.attachmentsNote ?? '',
          cargo: defaults.cargo ?? '',
          collateralType: defaults.collateralType ?? '',
          country: defaults.country ?? '',
          deliveryTermsTemplateId: String(
            defaults.deliveryTermsTemplateId ?? ''
          ),
          deliveryTimeNote: defaults.deliveryTimeNote ?? '',
          infoRows: defaults.infoRows ?? [],
          invoiceNote: defaults.invoiceNote ?? '',
          offerTextTemplateId: String(defaults.offerTextTemplateId ?? ''),
          priceListTemplateId: String(defaults.priceListTemplateId ?? ''),
          paymentTerm:
            defaults.paymentTerm === null ? '' : String(defaults.paymentTerm),
          promoTextEn: defaults.promoTextEn ?? '',
          promoTextFi: defaults.promoTextFi ?? '',
          promoTextSv: defaults.promoTextSv ?? '',
          signatureUser:
            defaults.signatureUser === null
              ? { label: '', value: '' }
              : {
                  label: `${defaults.signatureUser.firstName} ${defaults.signatureUser.lastName}`,
                  value: defaults.signatureUser.id,
                },
          validity: defaults.validity ?? '',
        });
        setSigner(defaults.signatureUser);
      })
      .catch(() => {
        setForm(emptyForm);
        setSigner(null);
        setSource('none');
      });
  };

  useEffect(load, [departmentId, kind, user.value]);

  // The own defaults of the kind and user are removed; the next ones are used instead
  const remove = async () => {
    if (departmentId === undefined || saving) {
      return;
    }

    setSaving(true);
    try {
      await apiOffers.defaults.delete(departmentId, kind, user.value || null);
      dispatch(addMessage(tr('Removed')));
      load();
    } catch (error) {
      dispatch(addMessage(tr('Save failed')));
    } finally {
      setSaving(false);
    }
  };

  // The details of a newly chosen signer as printed on the PDF
  const changeSigner = (user: { label: string; value: string } | null) => {
    setForm({ ...form, signatureUser: user ?? { label: '', value: '' } });

    if (user === null || user.value === '') {
      setSigner(null);
      return;
    }

    apiOffers.defaults
      .getSigner(user.value)
      .then(setSigner)
      .catch(() => setSigner(null));
  };

  const setField = (name: string) => (event: any) =>
    setForm({ ...form, [name]: event.target.value });

  const paymentTermValid =
    form.paymentTerm === '' || /^\d+$/.test(form.paymentTerm.trim());

  const save = async () => {
    if (departmentId === undefined || saving || !paymentTermValid) {
      return;
    }

    setSaving(true);
    try {
      await apiOffers.defaults.put(departmentId, {
        kind,
        userId: user.value || null,
        attachmentsNote: form.attachmentsNote,
        cargo: form.cargo,
        collateralType: form.collateralType === '' ? null : form.collateralType,
        country: form.country.trim() === '' ? null : form.country.trim(),
        deliveryTermsTemplateId: idOrNull(form.deliveryTermsTemplateId),
        deliveryTimeNote: form.deliveryTimeNote,
        infoRows: form.infoRows,
        invoiceNote: form.invoiceNote,
        offerTextTemplateId: idOrNull(form.offerTextTemplateId),
        priceListTemplateId: idOrNull(form.priceListTemplateId),
        paymentTerm:
          form.paymentTerm.trim() === ''
            ? null
            : parseInt(form.paymentTerm.trim(), 10),
        promoTextEn: form.promoTextEn,
        promoTextFi: form.promoTextFi,
        promoTextSv: form.promoTextSv,
        signatureUserId:
          form.signatureUser.value === '' ? null : form.signatureUser.value,
        validity: form.validity,
      });
      dispatch(addMessage(tr('Saved')));
      setSource('own');
    } catch (error) {
      dispatch(addMessage(tr('Save failed')));
    } finally {
      setSaving(false);
    }
  };

  // A choice from the text archive or the price list registry
  const templateSelect = (
    field:
      | 'deliveryTermsTemplateId'
      | 'offerTextTemplateId'
      | 'priceListTemplateId',
    choices: IChoice[]
  ) => (
    <Input
      id={`offer-defaults-${field}`}
      onChange={setField(field)}
      type="select"
      value={form[field]}
    >
      <option value="">{tr('None')}</option>
      {choices.map((item) => (
        <option key={item.id} value={String(item.id)}>
          {item.name}
        </option>
      ))}
    </Input>
  );

  // A row of the information table: the title at the left as in the PDF
  const row = (title: string, id: string, content: React.ReactNode) => (
    <div className="offer-defaults-row">
      <Label className="offer-defaults-row-title" for={id}>
        {title.toLocaleUpperCase()}
      </Label>
      <div className="offer-defaults-row-value">{content}</div>
    </div>
  );

  return (
    <PageContainer>
      <h1>{tr('Title')}</h1>
      <p>{tr('Info')}</p>
      {departments.length === 0 ? (
        <p>{tr('No departments')}</p>
      ) : (
        <>
          <FormGroup className="offer-defaults-department">
            <Label for="offer-defaults-department">{tr('Department')}</Label>
            <Input
              id="offer-defaults-department"
              onChange={(event) =>
                setDepartmentId(parseInt(event.target.value, 10))
              }
              type="select"
              value={departmentId ?? ''}
            >
              {departments.map((department) => (
                <option key={department.id} value={department.id}>
                  {department.name}
                </option>
              ))}
            </Input>
          </FormGroup>

          {/* For which offers and whom: the new offers of a user get the user's own
              defaults, otherwise those of the department */}
          <div className="offer-defaults-scope">
            <FormGroup>
              <Label for="offer-defaults-kind">{tr('Kind')}</Label>
              <Input
                id="offer-defaults-kind"
                onChange={(event) =>
                  setKind(event.target.value as OfferDefaultsKind)
                }
                type="select"
                value={kind}
              >
                <option value="offer">{tr('Kind offer')}</option>
                <option value="material-offer">
                  {tr('Kind material offer')}
                </option>
              </Input>
            </FormGroup>
            <FormGroup>
              <UserSelect
                label={tr('User')}
                onChange={(chosen: any) =>
                  setUser(chosen ?? { label: '', value: '' })
                }
                value={user}
              />
              <p className="offer-defaults-scope-info">
                {user.value ? tr('User info') : tr('Department info')}
              </p>
            </FormGroup>
          </div>
          <p
            className={`offer-defaults-source ${
              source === 'own' ? '' : 'not-own'
            }`}
          >
            {tr(`sources.${source}`)}
            {source === 'own' && (user.value || kind !== 'offer') ? (
              <PrimaryButton
                className="outline-button offer-defaults-remove"
                disabled={saving}
                onClick={remove}
                size="sm"
              >
                {tr('Remove own')}
              </PrimaryButton>
            ) : null}
          </p>

          {/* The end of the offer as on the PDF page */}
          <div className="offer-defaults-sheet">
            <p className="offer-defaults-sheet-info">{tr('Sheet info')}</p>

            <FormGroup>
              <Label for="offer-defaults-offerTextTemplateId">
                {tr('Offer text')}
              </Label>
              {templateSelect('offerTextTemplateId', offerTexts)}
            </FormGroup>

            {row(
              t(`${translations.offers}:Attachments`),
              'offer-defaults-attachments',
              <Input
                id="offer-defaults-attachments"
                onChange={setField('attachmentsNote')}
                value={form.attachmentsNote}
              />
            )}
            {row(
              t(`${translations.offers}:Cargo`),
              'offer-defaults-cargo',
              <Input
                id="offer-defaults-cargo"
                onChange={setField('cargo')}
                value={form.cargo}
              />
            )}
            {row(
              tr('Prices'),
              'offer-defaults-prices',
              <p className="offer-defaults-fixed" id="offer-defaults-prices">
                {tr('Prices info')}
              </p>
            )}
            {row(
              t(`${translations.offers}:Validity`),
              'offer-defaults-validity',
              <Input
                id="offer-defaults-validity"
                onChange={setField('validity')}
                value={form.validity}
              />
            )}
            {row(
              t(`${translations.offers}:Delivery time`),
              'offer-defaults-delivery-time',
              <Input
                id="offer-defaults-delivery-time"
                onChange={setField('deliveryTimeNote')}
                value={form.deliveryTimeNote}
              />
            )}
            {row(
              t(`${translations.offers}:Payment Term`),
              'offer-defaults-payment-term',
              <div className="offer-defaults-days">
                <Input
                  id="offer-defaults-payment-term"
                  invalid={!paymentTermValid}
                  onChange={setField('paymentTerm')}
                  value={form.paymentTerm}
                />
                <span>{tr('days')}</span>
              </div>
            )}
            {row(
              t(`${translations.offers}:Invoicing`),
              'offer-defaults-invoicing',
              <Input
                id="offer-defaults-invoicing"
                onChange={setField('invoiceNote')}
                value={form.invoiceNote}
              />
            )}
            {row(
              t(`${translations.offers}:Collateral`),
              'offer-defaults-collateral',
              <Input
                id="offer-defaults-collateral"
                onChange={setField('collateralType')}
                type="select"
                value={form.collateralType}
              >
                <option value="">{tr('None')}</option>
                {collateralTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Input>
            )}
            {/* The country of the address of a new offer, e.g. Sverige in Sweden */}
            {row(
              t(`${translations.offers}:Country`),
              'offer-defaults-country',
              <Input
                id="offer-defaults-country"
                onChange={setField('country')}
                placeholder="Suomi"
                value={form.country}
              />
            )}

            {/* Own rows after the collaterals: a title and a free text */}
            <OfferInfoRowsEditor
              onChange={(infoRows) => setForm({ ...form, infoRows })}
              rows={form.infoRows}
            />

            {/* The advertisement above the signature, in the language of the offer */}
            <div className="offer-defaults-row">
              <div className="offer-defaults-row-title" />
              <div className="offer-defaults-row-value">
                <p className="offer-defaults-promo-info">{tr('Promo info')}</p>
                <div className="offer-defaults-promo">
                  {PROMO_LANGUAGES.map((language) => (
                    <FormGroup key={language.field}>
                      <Label for={`offer-defaults-${language.field}`}>
                        {language.label}
                      </Label>
                      <Input
                        id={`offer-defaults-${language.field}`}
                        onChange={setField(language.field)}
                        rows={3}
                        type="textarea"
                        value={form[language.field]}
                      />
                    </FormGroup>
                  ))}
                </div>
              </div>
            </div>

            <div className="offer-defaults-row">
              <div className="offer-defaults-row-title" />
              <div className="offer-defaults-row-value">
                <p className="offer-defaults-fixed">{tr('Regards')}</p>
                <UserSelect
                  label={t(`${translations.offers}:Signature`)}
                  onChange={(user: any) => changeSigner(user)}
                  value={form.signatureUser}
                />
                {/* The signer as on the PDF */}
                {signer === null ? null : (
                  <p className="offer-defaults-signer">
                    {`${signer.firstName} ${signer.lastName}`}
                    {signer.companyName ? (
                      <>
                        <br />
                        {signer.companyName}
                      </>
                    ) : null}
                    {signer.phone ? (
                      <>
                        <br />
                        {signer.phone}
                      </>
                    ) : null}
                    {signer.email ? (
                      <>
                        <br />
                        {signer.email}
                      </>
                    ) : null}
                    <br />
                    www.inlook.fi
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Pages of their own after the offer */}
          <div className="offer-defaults-pages">
            <FormGroup>
              <Label for="offer-defaults-priceListTemplateId">
                {tr('Price list')}
              </Label>
              {templateSelect('priceListTemplateId', priceLists)}
            </FormGroup>
            <FormGroup>
              <Label for="offer-defaults-deliveryTermsTemplateId">
                {tr('Delivery terms')}
              </Label>
              {templateSelect('deliveryTermsTemplateId', deliveryTerms)}
            </FormGroup>
          </div>

          <PrimaryButton disabled={saving || !paymentTermValid} onClick={save}>
            {t(`${translations.common}:Save`).toLocaleUpperCase()}
          </PrimaryButton>
        </>
      )}
    </PageContainer>
  );
};

export default OfferDefaults;
